import { cn } from "@/utils/clientUtils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import Link from "next/link";
import * as React from "react";
const buttonVariants = cva("inline-flex items-center justify-center whitespace-nowrap rounded-md text-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary/90",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline"
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});

// Base props including HTML button attributes and variant props
interface BaseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

// Props for client-side button with onClick
interface ClientButtonProps extends BaseButtonProps {
  href?: never;
}

// Props for server-side button with href
interface ServerButtonProps extends BaseButtonProps {
  href: string;
  onClick?: never;
}
type ButtonProps = ClientButtonProps | ServerButtonProps;

/**
 * Button component that supports both client-side actions and server-side navigation.
 *
 * @example Server-side Navigation
 * ```tsx
 * <Button href="/some-page">Navigate</Button>
 * ```
 *
 * @example Client-side Action
 * ```tsx
 * <Button onClick={() => handleClick()}>Click Me</Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  href,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  const buttonClassName = cn(buttonVariants({
    variant,
    size,
    className
  }));
  if (href) {
    return <Link href={href} className={buttonClassName}>
          {props.children}
        </Link>;
  }
  return <Comp className={buttonClassName} ref={ref} {...props} />;
});
Button.displayName = "Button";
export { Button, buttonVariants };
export type { ButtonProps };